import { cn } from "@lib/cn";
import { FEATURE_FLAGS, useFeatureFlag } from "@lib/feature-flags";
import {
  IconAlertCircleFilled,
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconInfoCircleFilled,
  type TablerIcon,
} from "@tabler/icons-react";
import { Bounce, ToastContainer, type TypeOptions } from "react-toastify";

// Secondary container for full-width announcement banners (e.g. the PDF
// export progress bar). Toasts only land here when fired with this
// containerId; toasts without a containerId keep using the default
// container below.
export const WIDE_TOAST_CONTAINER_ID = "wide-banner";

/** A toast's title and a muted line under it (Figma 198:274663). */
export const ToastText = ({ title, text }: { title: string; text: string }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-font-sans tw-text-sm/5">
    <div className="tw-font-medium">{title}</div>
    <div className="tw-font-normal tw-text-blue-500">{text}</div>
  </div>
);

// Send flow (Figma 198:264027): a tinted card per type, bottom right. Only
// typed toasts; a custom card (toast(<Card />, { className })) styles itself.
const TOAST_TONES: Partial<
  Record<
    TypeOptions,
    { icon: TablerIcon; iconClassName: string; toastClassName: string }
  >
> = {
  success: {
    icon: IconCircleCheckFilled,
    iconClassName: "tw-text-green-500",
    toastClassName: "tw-bg-green-50",
  },
  error: {
    icon: IconAlertCircleFilled,
    iconClassName: "tw-text-red-base",
    toastClassName: "tw-bg-red-50",
  },
  warning: {
    icon: IconAlertTriangleFilled,
    iconClassName: "tw-text-golden-500",
    toastClassName: "tw-bg-golden-50",
  },
  info: {
    icon: IconInfoCircleFilled,
    iconClassName: "tw-text-primary-base",
    toastClassName: "tw-bg-primary-50",
  },
};

const TOAST_CARD =
  "tw-mb-2 tw-ml-auto tw-mr-0 tw-w-fit tw-max-w-[min(400px,calc(100vw-32px))] tw-rounded-xl tw-border-0 tw-px-4 tw-py-3 tw-text-left tw-text-sm tw-font-medium tw-text-blue-base tw-shadow-xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-base/5 last:tw-mb-0";
const TOAST_CARD_BODY = "tw-m-0 tw-p-0";

const Toast = () => {
  const isSendFlow = useFeatureFlag(FEATURE_FLAGS.SEND_FLOW);
  const toneOf = (type?: TypeOptions) =>
    isSendFlow && type ? TOAST_TONES[type] : undefined;

  return (
    <>
      <ToastContainer
        icon={({ type }) => {
          const tone = toneOf(type);
          if (tone) {
            const ToneIcon = tone.icon;
            // tw-size-6 beats the global 16px svg.tabler-icon; the icon
            // wrapper's own 8px margin + tw-mr-1 = Figma's 12px gap.
            return (
              <ToneIcon
                className={cn("tw-mr-1 tw-size-6", tone.iconClassName)}
              />
            );
          }
          const icons: Record<string, string> = {
            success: "ri-checkbox-circle-fill",
            error: "ri-error-warning-fill",
            info: "ri-information-fill",
            warning: "ri-alert-fill",
          };
          const icon = icons[type];
          return icon ? <i className={icon} /> : null;
        }}
        className={cn(isSendFlow && "tw-z-[999999]")}
        toastClassName={(context) => {
          const tone = toneOf(context?.type);
          return cn(
            context?.defaultClassName,
            tone && TOAST_CARD,
            tone?.toastClassName,
          );
        }}
        // The body's function gets only { type }, so its default class goes in by hand.
        bodyClassName={(context) =>
          cn("Toastify__toast-body", toneOf(context?.type) && TOAST_CARD_BODY)
        }
        position={isSendFlow ? "bottom-right" : "bottom-center"}
        hideProgressBar
        closeButton={false}
        closeOnClick
        autoClose={3000}
        draggable={false}
        theme="light"
        transition={Bounce}
      />
      <ToastContainer
        containerId={WIDE_TOAST_CONTAINER_ID}
        className="!tw-w-[min(857px,calc(100vw-32px))]"
        position="bottom-center"
        hideProgressBar
        closeButton={false}
        closeOnClick={false}
        autoClose={false}
        draggable={false}
        theme="light"
        transition={Bounce}
      />
    </>
  );
};

export default Toast;
